import React, { useEffect, useState, useCallback, useRef } from "react";

import { useNavigate } from "react-router-dom";
import ClinicPartnerHeader from "../../component/clinicPartnerHeader";
import { Constant } from "../../../../../../helper/constant";
import PaginationFooter from "../../../../../../components/footer/paginationFooter";
import SalesSummary from "../../../../common_component/salesSummary";
import ModalDetailClinicSales from "../../../clinic-sales/component/modalDetailClinicSales";
import SalesClinicData from "../../../clinic-sales/component/salesClinicData";
import { getInitialDate } from "../../../../../../helper/formatDate";
import SalesClinicReceipt from "../../../clinic-sales/component/salesClinicReceipt";

const ClinicPartnerRanged = () => {
  // =====================
  // Constant Definition
  // =====================

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [transactionData, setTransactionData] = useState([]);
  const [summaryTransaction, setSummaryTransaction] = useState(null);
  const [filterData, setFilterData] = useState(null);
  const [transactionToPrint, setTransactionToPrint] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [printTrigger, setPrintTrigger] = useState(false);
  const [isShowDetailTransactionModal, setIsShowDetailTransactionModal] =
    useState(false);
  const receiptRef = useRef();
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [partnerName, setPartnerName] = useState("");

  // =====================
  // Method
  // =====================

  const getTransactionDate = () => {
    if (filterData !== null) {
      return filterData.date;
    } else {
      return getInitialDate("monthly");
    }
  };

  const onClickPrintTransaction = (transactionData) => {
    setTransactionToPrint(transactionData);
    setPrintTrigger((prev) => !prev);
  };

  const onOpenDetailTransaction = (transactionData) => {
    setSelectedTransaction(transactionData);
    setIsShowDetailTransactionModal(true);
  };

  const onCloseModalDetailTransaction = () => {
    setIsShowDetailTransactionModal(false);
    setSelectedTransaction(null);
  };

  const onDeleteClinicTransaction = (idToDelete) => {
    deleteTransaction(idToDelete);
  };

  // Custom print function
  const handlePrint = useCallback(async () => {
    if (receiptRef.current) {
      const printWindow = window.open("", "_blank");

      // Write the HTML structure and CSS link
      printWindow.document.write(`
          <html>
            <head>
              <title>Resep Klinik ${transactionToPrint.medical_transaction_invoice_number}</title>
              <link rel="stylesheet" href="/css/productReceipt/salesReceipt.css">
            </head>
            <body>
              <div class="receipt">${receiptRef.current.innerHTML}</div>
            </body>
          </html>
        `);

      // Close the document to ensure styles are loaded
      printWindow.document.close();

      // Add a small delay to ensure styles are applied before printing
      setTimeout(() => {
        printWindow.print();
      }, 500); // Adjust this timeout if necessary
    }
  }, [transactionToPrint]);

  const onClickSearch = (queryJson, partnerName) => {
    setPartnerName(partnerName);
    setFilterData(queryJson);
    setPage(1);
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Network
  // =====================

  const getInitialTransaction = useCallback(async () => {
    try {
      setIsLoading(true);
      let url;
      const params = new URLSearchParams({
        start_date: filterData.start_date,
        end_date: filterData.end_date,
        partner_id: filterData.partner_id || "",
        page: page,
      }).toString();
      url = `${Constant.BASE_API_URL}/tindakan-medis-transaksi/range-partner?${params}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;

      if (!data.medical_transactions) {
        setTransactionData([]);
      } else {
        setTransactionData(data.medical_transactions);
        setSummaryTransaction(data.summary);
      }
      setTotalPage(data.total_pages);
    } catch (error) {
      setTransactionData([]);
      setSummaryTransaction(null);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, filterData, page]);

  const deleteTransaction = async (medicalTransactionId) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        Constant.BASE_API_URL +
          "/tindakan-medis-transaksi/" +
          medicalTransactionId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getInitialTransaction();
    }
  };

  // =====================
  // UseEffect Hook
  // =====================

  useEffect(() => {
    if (transactionToPrint) {
      handlePrint();
    }
  }, [transactionToPrint, printTrigger, handlePrint]);

  useEffect(() => {
    getInitialTransaction();
  }, [filterData, page, getInitialTransaction]);

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Laporan Klinik Rekanan Dalam Rentang Hari</h1>
      <ClinicPartnerHeader
        typeOfSales={"ranged"}
        onClickSearch={onClickSearch}
      />
      <p>Rekanan yang dicari: {partnerName}</p>
      <SalesClinicData
        transactionDate={getTransactionDate()}
        transactionData={transactionData}
        onClickPrintTransaction={onClickPrintTransaction}
        onOpenDetailTransaction={onOpenDetailTransaction}
        onDeleteClinicTransaction={onDeleteClinicTransaction}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <SalesSummary summaryData={summaryTransaction} />
      <ModalDetailClinicSales
        isShowModal={isShowDetailTransactionModal}
        clinicTransaction={selectedTransaction}
        onCloseModal={onCloseModalDetailTransaction}
      />

      <div style={{ display: "none" }}>
        <SalesClinicReceipt
          ref={receiptRef}
          transactionData={transactionToPrint}
        />
      </div>
    </div>
  );
};

export default ClinicPartnerRanged;
